export type Language = "pl" | "en";

export interface TranslationSchema {
  nav: { login: string };
  hero: {
    eyebrow: string;
    headlineWords: string[];
    headlineAccent: string;
    description: string;
    stats: Array<{ label: string; sub: string }>;
    cta: string;
    ctaNote: string;
    chatHeader: string;
    chatMessages: Array<{ sender: "client" | "ai"; text: string }>;
  };
  problem: {
    heading: string;
    items: string[];
    conclusion: string;
    conclusionAccent: string;
  };
  solution: {
    heading: string;
    headingAccent: string;
    description: string;
    items: Array<{ title: string; description: string }>;
    closing: string;
    closingAccent: string;
  };
  channels: {
    heading: string;
    body: string;
    subtext: string;
  };
  conversations: {
    cards: Array<{ title: string; messages: Array<{ sender: "client" | "ai"; text: string }> }>;
  };
  steps: {
    heading: string;
    items: Array<{ title: string; description: string }>;
  };
  audience: {
    heading: string;
    yesLabel: string;
    noLabel: string;
    yes: string[];
    no: string[];
  };
  outcome: {
    heading: string;
    items: string[];
    closing: string;
    closingAccent: string;
    cta: string;
  };
  faq: {
    heading: string;
    items: Array<{ question: string; answer: string }>;
  };
  finalCta: {
    heading: string;
    subheading: string;
    cta: string;
  };
  contact: {
    heading: string;
    headingAccent: string;
    subheading: string;
    labelName: string;
    placeholderName: string;
    labelPhone: string;
    placeholderPhone: string;
    submitIdle: string;
    submitLoading: string;
    successHeading: string;
    successBody: string;
    errorMessage: string;
    footnote: string;
  };
  footer: { tagline: string; privacyPolicy: string; terms: string; copyright: string };
  legal: { backToHome: string };
}

export const translations: Record<Language, TranslationSchema> = {
  pl: {
    nav: {login: "Zaloguj się"},
    hero: {
      eyebrow: "Asystent AI dla salonów beauty",
      headlineWords: ["Zamień", "wiadomości", "na", "Instagramie", "i", "Messengerze", "w"],
      headlineAccent: "zapisanych klientów",
      description:
        "Automatyczny asystent, który odpowiada klientom i umawia wizyty za Ciebie 24/7.",
      stats: [
        {label: "Dostępność 24/7", sub: "zawsze aktywny"},
        {label: "Naturalne rozmowy", sub: "jak człowiek"},
        {label: "Pełna automatyzacja", sub: "bez Twojego udziału"},
      ],
      cta: "Zacznij teraz",
      ctaNote: "Bez zobowiązań. Start w kilka minut",
      chatHeader: "Rezerwacja wizyty",
      chatMessages: [
        {sender: "client", text: "Chciałabym umówić się na manicure."},
        {sender: "ai", text: "Środa 15:30 jest wolna. Rezerwować?"},
        {sender: "client", text: "Tak, poproszę!"},
        {sender: "ai", text: "Zarezerwowałam wizytę na środę 26.04 o 15:30. Do zobaczenia! 💅"},
      ],
    },
    problem: {
      heading: "Masz dość tego, że:",
      items: [
        "odpisujesz klientom w nocy, po godzinach",
        "ciągle odpowiadasz na te same pytania",
        "klienci pytają o cenę i znikają",
        "gubisz zapisy w wiadomościach",
      ],
      conclusion: "Problemem nie jest brak klientów.",
      conclusionAccent: "Problemem jest czas, który poświęcasz na ich obsługę.",
    },
    solution: {
      heading: "Poznaj",
      headingAccent: "Assistly",
      description:
        "Asystent, który odpisuje klientom za Ciebie na Messengerze i Instagramie.",
      items: [
        {
          title: "Automatyczna obsługa zapytań",
          description: "Klienci otrzymują szybkie odpowiedzi na zapytania.",
        },
        {
          title: "Inteligentne proponowanie terminów",
          description: "System sugeruje dostępne sloty, skracając proces decyzji.",
        },
        {
          title: "Więcej rozmów kończy się wizytą",
          description:
            "Assistly prowadzi rozmowę do konkretu — proponuje termin zamiast czekać na decyzję klienta.",
        },
        {
          title: "Stała dostępność",
          description:
            "Komunikacja działa nieprzerwanie, niezależnie od godzin pracy salonu.",
        },
      ],
      closing: "Ty skupiasz się na klientkach,",
      closingAccent: "Assistly ogarnia resztę.",
    },
    channels: {
      heading: "Działa tam, gdzie są Twoi klienci",
      body: "Instagram i Messenger, bez aplikacji, bez zmiany przyzwyczajeń.",
      subtext: "Klient pisze jak zawsze. Odpowiedź przychodzi automatycznie. Bez Twojego udziału.",
    },
    conversations: {
      cards: [
        {
          title: "Rezerwacja wizyty",
          messages: [
            {sender: "client", text: "Cześć! Chciałabym umówić się na manicure."},
            {sender: "ai", text: "Witam! Z przyjemnością umówię wizytę. Który dzień Pani pasuje?"},
            {sender: "client", text: "Może środa około 15?"},
            {sender: "ai", text: "Środa 15:30 jest wolna. Manicure hybrydowy zajmuje około godziny. Zarezerwować?"},
            {sender: "client", text: "Tak, super!"},
            {
              sender: "ai",
              text: "Doskonale! Zarezerwowałam wizytę na środę 26.04 o 15:30. Przypomnę dzień wcześniej. Do zobaczenia! 💅"
            },
          ],
        },
        {
          title: "Pytanie o cenę",
          messages: [
            {sender: "client", text: "Ile kosztuje przedłużanie rzęs?"},
            {
              sender: "ai",
              text: "Przedłużanie rzęs metodą 1:1 kosztuje 180 zł, a metodą objętościową 2D-5D — 220 zł. Która metoda Panią interesuje?"
            },
            {sender: "client", text: "Czym się różnią?"},
            {
              sender: "ai",
              text: "Metoda 1:1 to naturalne przedłużenie. Objętościowa daje efekt bardziej dramatyczny i gęsty. Mogę zarezerwować wizytę konsultacyjną?"
            },
          ],
        },
        {
          title: "Zmiana terminu",
          messages: [
            {sender: "client", text: "Mam wizytę jutro o 10, ale czy mogę przełożyć na 14?"},
            {
              sender: "ai",
              text: "Sprawdzam... Tak, 14:00 jest wolne! Przesuwam Pani wizytę z jutra 10:00 na 14:00. Wszystko gotowe!"
            },
            {sender: "client", text: "Dziękuję!"},
            {sender: "ai", text: "Nie ma za co! Do zobaczenia jutro o 14:00 😊"},
          ],
        },
      ],
    },
    steps: {
      heading: "Jak to działa?",
      items: [
        {
          title: "Podłączasz Instagram i Messenger",
          description: "Kilka kliknięć, bez instalowania czegokolwiek.",
        },
        {
          title: "Ustawiamy odpowiedzi i ofertę",
          description: "Usługi, ceny i grafik — tak, aby AI mówiła Twoim językiem.",
        },
        {
          title: "Assistly pracuje dla Ciebie",
          description: "Odpowiada, umawia wizyty i pilnuje kalendarza — bez Twojego udziału.",
        },
      ],
    },
    audience: {
      heading: "Czy to dla Ciebie?",
      yesLabel: "Tak, jeśli:",
      noLabel: "Nie, jeśli:",
      yes: [
        "prowadzisz salon beauty / barber / kosmetykę",
        "klienci piszą do Ciebie przez Instagram lub Messenger",
        "zamiast pracować lub odpoczywać — odpisujesz na wiadomości",
      ],
      no: [
        "nie masz klientów online",
        "nie używasz social mediów",
        "nie przyjmujesz zapisów przez wiadomości",
      ],
    },
    outcome: {
      heading: "Co się zmienia?",
      items: [
        "szybsza i sprawniejsza obsługa klientów",
        "więcej zapisów bez Twojego udziału",
        "masz mniej wiadomości do odpisywania",
        "po prostu więcej czasu dla Ciebie",
      ],
      closing: "Zaopiekowani klienci. Mniej odpisywania.",
      closingAccent: "Więcej czasu na rozwój Twojego salonu.",
      cta: "Zacznij teraz",
    },
    faq: {
      heading: "Pytania i odpowiedzi",
      items: [
        {
          question: "Czy poradzę sobie z obsługą tego?",
          answer: "Jasne, my pomagamy wszystko ustawić.",
        },
        {
          question: "Czy mogę to wyłączyć?",
          answer: "Tak, masz pełną kontrolę na każdym etapie korzystania z Assistly.",
        },
        {
          question: "Czy to działa automatycznie?",
          answer: "Tak, 24/7.",
        },
      ],
    },
    finalCta: {
      heading: "Przestań tracić czas na powtarzalne wiadomości",
      subheading: "Skup się na tym, co naprawdę zarabia.",
      cta: "Uruchom teraz",
    },
    contact: {
      heading: "Gotowy na",
      headingAccent: "automatyzację",
      subheading: "Zostaw swoje dane, a skontaktujemy się z Tobą, aby omówić szczegóły",
      labelName: "Imię i Nazwisko / Nazwa Firmy",
      placeholderName: "Jan Kowalski / Salon Piękności",
      labelPhone: "Numer telefonu",
      placeholderPhone: "+48 123 456 789",
      submitIdle: "Wyślij zgłoszenie",
      submitLoading: "Wysyłanie...",
      successHeading: "Dziękujemy!",
      successBody: "Skontaktujemy się z Tobą wkrótce.",
      errorMessage: "Wystąpił błąd. Spróbuj ponownie.",
      footnote: "Odpowiemy w ciągu 24 godzin · Twoje dane są bezpieczne",
    },
    footer: {
      tagline: "Inteligentna obsługa rezerwacji dla salonów beauty",
      privacyPolicy: "Polityka prywatności",
      terms: "Regulamin",
      copyright: "© 2025 Assistly. Wszelkie prawa zastrzeżone.",
    },
    legal: {backToHome: "← Powrót"},
  },

  en: {
    nav: {login: "Log in"},
    hero: {
      eyebrow: "AI Assistant for beauty salons",
      headlineWords: ["Turn", "Instagram", "and", "Messenger", "messages", "into"],
      headlineAccent: "booked clients",
      description:
        "An automated assistant that answers your clients and books appointments for you 24/7.",
      stats: [
        {label: "24/7 Availability", sub: "always active"},
        {label: "Natural conversations", sub: "like a human"},
        {label: "Full automation", sub: "without your involvement"},
      ],
      cta: "Get started",
      ctaNote: "No commitment. Set up in minutes",
      chatHeader: "Appointment booking",
      chatMessages: [
        {sender: "client", text: "I'd like to book a manicure appointment."},
        {sender: "ai", text: "Wednesday at 3:30 PM is free. Shall I book it?"},
        {sender: "client", text: "Yes, please!"},
        {sender: "ai", text: "I've booked your appointment for Wednesday 26.04 at 3:30 PM. See you then! 💅"},
      ],
    },
    problem: {
      heading: "Are you tired of:",
      items: [
        "replying to clients at night, after hours",
        "answering the same questions over and over",
        "clients asking about the price and disappearing",
        "losing bookings somewhere in your inbox",
      ],
      conclusion: "The problem isn't a lack of clients.",
      conclusionAccent: "The problem is the time you spend handling them.",
    },
    solution: {
      heading: "Meet",
      headingAccent: "Assistly",
      description:
        "An assistant that replies to your clients on Messenger and Instagram for you.",
      items: [
        {
          title: "Automatic handling of enquiries",
          description: "Clients get fast answers to their questions.",
        },
        {
          title: "Smart slot suggestions",
          description: "The system proposes available slots, shortening the decision.",
        },
        {
          title: "More conversations end in a booking",
          description:
            "Assistly steers the conversation towards a decision — it offers a slot instead of waiting for the client.",
        },
        {
          title: "Always available",
          description:
            "Communication never stops, regardless of your salon's opening hours.",
        },
      ],
      closing: "You focus on your clients,",
      closingAccent: "Assistly handles the rest.",
    },
    channels: {
      heading: "It works where your clients already are",
      body: "Instagram and Messenger — no app, no new habits.",
      subtext: "Your client writes as always. The reply arrives automatically. Without you.",
    },
    conversations: {
      cards: [
        {
          title: "Appointment booking",
          messages: [
            {sender: "client", text: "Hi! I'd like to book a manicure."},
            {sender: "ai", text: "Hello! I'd be happy to book an appointment. Which day works for you?"},
            {sender: "client", text: "Maybe Wednesday around 3?"},
            {
              sender: "ai",
              text: "Wednesday at 3:30 PM is free. A hybrid manicure takes about an hour. Shall I book it?"
            },
            {sender: "client", text: "Yes, great!"},
            {
              sender: "ai",
              text: "Done! I've booked your appointment for Wednesday 26.04 at 3:30 PM. I'll remind you the day before. See you then! 💅"
            },
          ],
        },
        {
          title: "Price inquiry",
          messages: [
            {sender: "client", text: "How much does eyelash extension cost?"},
            {
              sender: "ai",
              text: "Classic 1:1 eyelash extensions are £80, and volume 2D-5D are £100. Which method interests you?"
            },
            {sender: "client", text: "What's the difference?"},
            {
              sender: "ai",
              text: "Classic gives a natural look. Volume creates a more dramatic, fuller effect. Would you like to book a consultation?"
            },
          ],
        },
        {
          title: "Rescheduling",
          messages: [
            {sender: "client", text: "I have an appointment tomorrow at 10, can I move it to 2 PM?"},
            {
              sender: "ai",
              text: "Checking... Yes, 2:00 PM is available! I've moved your appointment from 10:00 AM to 2:00 PM tomorrow. All done!"
            },
            {sender: "client", text: "Thank you!"},
            {sender: "ai", text: "My pleasure! See you tomorrow at 2:00 PM 😊"},
          ],
        },
      ],
    },
    steps: {
      heading: "How does it work?",
      items: [
        {
          title: "You connect Instagram and Messenger",
          description: "A few clicks, nothing to install.",
        },
        {
          title: "We set up the replies and your offer",
          description: "Services, prices and schedule — so the AI speaks your language.",
        },
        {
          title: "Assistly works for you",
          description: "It replies, books appointments and keeps the calendar — without you.",
        },
      ],
    },
    audience: {
      heading: "Is this for you?",
      yesLabel: "Yes, if:",
      noLabel: "No, if:",
      yes: [
        "you run a beauty salon, barber shop or cosmetics studio",
        "clients message you on Instagram or Messenger",
        "instead of working or resting — you're replying to messages",
      ],
      no: [
        "you have no clients online",
        "you don't use social media",
        "you don't take bookings through messages",
      ],
    },
    outcome: {
      heading: "What changes?",
      items: [
        "faster, smoother client service",
        "more bookings without your involvement",
        "fewer messages to reply to",
        "simply more time for you",
      ],
      closing: "Clients taken care of. Less replying.",
      closingAccent: "More time to grow your salon.",
      cta: "Get started",
    },
    faq: {
      heading: "Questions and answers",
      items: [
        {
          question: "Will I be able to handle it?",
          answer: "Of course — we help you set everything up.",
        },
        {
          question: "Can I turn it off?",
          answer: "Yes, you stay in full control at every step of using Assistly.",
        },
        {
          question: "Does it work automatically?",
          answer: "Yes, 24/7.",
        },
      ],
    },
    finalCta: {
      heading: "Stop losing time on repetitive messages",
      subheading: "Focus on what actually makes money.",
      cta: "Get it running",
    },
    contact: {
      heading: "Ready for",
      headingAccent: "automation",
      subheading: "Leave your details and we'll get in touch to discuss everything",
      labelName: "Full Name / Company Name",
      placeholderName: "Jane Smith / Beauty Salon",
      labelPhone: "Phone number",
      placeholderPhone: "+1 234 567 890",
      submitIdle: "Send request",
      submitLoading: "Sending...",
      successHeading: "Thank you!",
      successBody: "We'll be in touch soon.",
      errorMessage: "Something went wrong. Please try again.",
      footnote: "We'll respond within 24 hours · Your data is safe",
    },
    footer: {
      tagline: "Intelligent booking management for beauty salons",
      privacyPolicy: "Privacy Policy",
      terms: "Terms of Use",
      copyright: "© 2025 Assistly. All rights reserved.",
    },
    legal: {backToHome: "← Back"},
  },
};
